import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Boxes, ShoppingCart, Wrench, Settings as SettingsIcon,
  LogOut, Menu, X, Users, Wallet, UserCog, FileText, ChevronDown, Package, ReceiptText, Truck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import NotificationsBell from "@/components/NotificationsBell";

const NAV = [
  { type: "link", to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  {
    type: "group", label: "Sales & Billing", icon: ShoppingCart, testId: "nav-group-sales",
    items: [
      { to: "/inventory", label: "Inventory & Sales", icon: Boxes, testId: "nav-inventory" },
      { to: "/quotes", label: "Quotations", icon: FileText, testId: "nav-quotes" },
      { to: "/invoices", label: "Tax Invoices", icon: ReceiptText, testId: "nav-invoices" },
      { to: "/item-masters", label: "Item Masters", icon: Package, testId: "nav-item-masters" },
    ],
  },
  { type: "link", to: "/customers", label: "Customers", icon: Users, testId: "nav-customers" },
  { type: "link", to: "/services", label: "Services", icon: Wrench, testId: "nav-services" },
  {
    type: "group", label: "Operations", icon: Wallet, testId: "nav-group-ops",
    items: [
      { to: "/expenses", label: "Expenses", icon: Wallet, testId: "nav-expenses" },
      { to: "/staff", label: "Staff", icon: UserCog, testId: "nav-staff" },
      { to: "/delivery", label: "Delivery Labels", icon: Truck, testId: "nav-delivery" },
    ],
  },
  { type: "link", to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings", adminOnly: true },
];

const linkBase =
  "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap border-b-2";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navLinkClass = ({ isActive }) =>
    cn(
      linkBase,
      isActive
        ? "bg-[#F1F5F9] text-[#0A0A0A] border-b-[#0055FF] font-medium"
        : "text-[#475569] hover:bg-slate-50 border-b-transparent"
    );

  const isChildActive = (items) =>
    items.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + "/"));

  const isAdmin = user?.role === "admin";
  const visibleNav = NAV.map((n) => {
    if (n.type === "link") return (n.adminOnly && !isAdmin) ? null : n;
    const items = n.items.filter((c) => !(c.adminOnly && !isAdmin));
    return items.length ? { ...n, items } : null;
  }).filter(Boolean);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0]">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6">
          <div className="h-16 flex items-center justify-between gap-4">
            {/* Logo */}
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-8 h-8 rounded-md bg-[#0055FF] flex items-center justify-center text-white font-bold">D</div>
              <div className="leading-tight">
                <div className="text-sm font-semibold tracking-tight">Digital <span className="text-[#0055FF]">Yantra</span></div>
                <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-mono hidden sm:block">Store Manager</div>
              </div>
            </div>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
              {visibleNav.map((n) =>
                n.type === "link" ? (
                  <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testId} className={navLinkClass}>
                    <n.icon className="w-4 h-4" strokeWidth={1.75} />
                    {n.label}
                  </NavLink>
                ) : (
                  <DropdownMenu key={n.label}>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={n.testId}
                        className={cn(
                          linkBase, "outline-none cursor-pointer",
                          isChildActive(n.items)
                            ? "bg-[#F1F5F9] text-[#0A0A0A] border-b-[#0055FF] font-medium"
                            : "text-[#475569] hover:bg-slate-50 border-b-transparent"
                        )}
                      >
                        <n.icon className="w-4 h-4" strokeWidth={1.75} />
                        {n.label}
                        <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      {n.items.map((c) => (
                        <DropdownMenuItem key={c.to} asChild>
                          <NavLink
                            to={c.to}
                            data-testid={`${c.testId}-menu`}
                            className={({ isActive }) =>
                              cn("flex items-center gap-2 cursor-pointer w-full", isActive && "text-[#0055FF] font-medium")}
                          >
                            <c.icon className="w-4 h-4" strokeWidth={1.75} />
                            {c.label}
                          </NavLink>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              )}
            </nav>

            {/* Right controls */}
            <div className="flex items-center gap-2 md:gap-3 shrink-0">
              <NotificationsBell />
              <div className="hidden md:flex flex-col items-end leading-tight">
                <div className="text-xs font-medium text-[#0A0A0A] truncate max-w-[140px]" data-testid="user-name">{user?.name || "Administrator"}</div>
                <div className="text-[11px] text-[#64748B] truncate max-w-[140px] font-mono" data-testid="user-email">{user?.email}</div>
              </div>
              <Button variant="outline" size="sm" className="hidden lg:inline-flex gap-2 h-9" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" /> Sign out
              </Button>
              <button className="lg:hidden p-2 -mr-1" onClick={() => setOpen(!open)} data-testid="open-nav" aria-label="Toggle navigation">
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile / tablet nav */}
        {open && (
          <div className="lg:hidden border-t border-[#E2E8F0] bg-white max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-nav">
            <nav className="px-4 py-3 space-y-1 max-w-[1600px] mx-auto">
              {visibleNav.map((n) =>
                n.type === "link" ? (
                  <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`m-${n.testId}`} className={navLinkClass}>
                    <n.icon className="w-4 h-4" strokeWidth={1.75} />
                    {n.label}
                  </NavLink>
                ) : (
                  <div key={n.label} className="pt-2">
                    <div className="px-3 pb-1 text-[10px] font-mono uppercase tracking-wider text-[#94A3B8]">{n.label}</div>
                    {n.items.map((c) => (
                      <NavLink key={c.to} to={c.to} onClick={() => setOpen(false)} data-testid={`m-${c.testId}`} className={navLinkClass}>
                        <c.icon className="w-4 h-4" strokeWidth={1.75} />
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )
              )}
              <div className="pt-3 mt-2 border-t border-[#E2E8F0]">
                <div className="px-1 py-1">
                  <div className="text-xs font-medium truncate" data-testid="m-user-name">{user?.name || "Administrator"}</div>
                  <div className="text-[11px] text-[#64748B] truncate font-mono" data-testid="m-user-email">{user?.email}</div>
                </div>
                <Button variant="outline" className="w-full justify-start gap-2 h-9 mt-2" onClick={handleLogout} data-testid="m-logout-btn">
                  <LogOut className="w-4 h-4" /> Sign out
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 px-4 md:px-6 py-6 pb-20 max-w-[1600px] mx-auto w-full">
        <Outlet />
      </main>
    </div>
  );
}
